<script setup lang="ts">
import { adjustForCustomers } from '@revolugo/common/cancellation-policies'
import { dayjs } from '@revolugo/common/utils'
import { BookingStatus } from '@revolugo/graphql/private'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useMoney } from '#composables'
import { CancellationPoliciesStatus, RAlertSeverity } from '#types'

import RAlert from '../RAlert/RAlert.vue'

import type {
  CancellationPolicy,
  CurrencyGraphQl,
} from '@revolugo/graphql/private'

const { locale, t } = useI18n()

const props = defineProps<{
  cancellationPolicies: CancellationPolicy[]
  price: number
  currency: CurrencyGraphQl
  bookingStatus: BookingStatus
}>()

const adjustedCancellationPolicies = computed(() =>
  adjustForCustomers(props.cancellationPolicies),
)

function isCurrentDateBetween(dateFrom: string, dateTo: string): boolean {
  return dayjs().isBetween(dateFrom, dateTo, undefined, '[)')
}

function getFormattedPriceWithPenalty(penalty: number): string | null {
  const penaltyAmount = props.price * (penalty / 100)
  const value = props.price - penaltyAmount
  const { formattedValue } = useMoney(
    {
      currency: props.currency,
      value,
    },
    locale,
  )
  return formattedValue.value
}

function cancellationPoliciesStatus(cancellationPolicy: CancellationPolicy): {
  [key: string]: {
    label: string
    severity: RAlertSeverity
  }
} {
  const { dateFrom, dateTo, penaltyPercentage } = cancellationPolicy
  return {
    [CancellationPoliciesStatus.Free]: {
      label: t('FREE', {
        dateTo: dayjs(dateTo).locale(locale.value).format('llll'),
      }),
      severity: RAlertSeverity.Success,
    },
    [CancellationPoliciesStatus.NoRefund]: {
      label: t('NO_REFUND', {
        dateFrom: dayjs(dateFrom).locale(locale.value).format('llll'),
      }),
      severity: RAlertSeverity.Error,
    },
    [CancellationPoliciesStatus.Partial]: {
      label: t('PARTIAL', {
        dateFrom: dayjs(dateFrom).locale(locale.value).format('llll'),
        dateTo: dayjs(dateTo).locale(locale.value).format('llll'),
        percentage: 100 - penaltyPercentage,
        price: getFormattedPriceWithPenalty(penaltyPercentage),
      }),
      severity: RAlertSeverity.Warn,
    },
  }
}

function getCancellationPolicyStatus(cancellationPolicy: CancellationPolicy): {
  label: string
  severity: RAlertSeverity
} {
  switch (cancellationPolicy.penaltyPercentage) {
    case 0:
      return cancellationPoliciesStatus(cancellationPolicy)[
        CancellationPoliciesStatus.Free
      ]
    case 100:
      return cancellationPoliciesStatus(cancellationPolicy)[
        CancellationPoliciesStatus.NoRefund
      ]
    default:
      return cancellationPoliciesStatus(cancellationPolicy)[
        CancellationPoliciesStatus.Partial
      ]
  }
}
</script>

<template>
  <div data-testid="r-cancellation-policies-list">
    <div
      v-for="(cancellationPolicy, index) in adjustedCancellationPolicies"
      :key="index"
      class="mb-4"
    >
      <RAlert
        v-if="
          isCurrentDateBetween(
            cancellationPolicy.dateFrom,
            cancellationPolicy.dateTo,
          ) && bookingStatus !== BookingStatus.Canceled
        "
        :data-testid="`r-cancellation-policies-list-item-message-${index}`"
        :severity="getCancellationPolicyStatus(cancellationPolicy).severity"
        class="text-sm text-neutral-600"
      >
        {{ getCancellationPolicyStatus(cancellationPolicy).label }}
      </RAlert>
      <p
        v-else
        class="text-sm"
        :data-testid="`r-cancellation-policies-list-item-message-${index}`"
      >
        {{ getCancellationPolicyStatus(cancellationPolicy).label }}
      </p>
    </div>
  </div>
</template>

<i18n src="./RCancellationPoliciesList.i18n.json"></i18n>
